<template>
  <div class="w-72">
    <div
      :ref="`output-${output.id}`"
      class="relative grid grid-cols-[36px_1fr] gap-2 h-12 pl-1 items-center overflow-hidden rounded-md cursor-pointer transition-all duration-200 select-none group"
      :title="output.displayName || output.name"
      :class="{
        'bg-slate-700': isOutputRouted,
        'bg-slate-800': !isOutputRouted,
        'ring-2 ring-green-400': isOutputSelected,
        'ring-2 ring-blue-400': activeInputOutputs.includes(output.id),
        'ring-2 ring-blue-400': selectedOutputIds.includes(output.id),
        'opacity-50 pointer-events-none': !activeInputOutputs && activeInputOutputs.id !== output.id,
      }"
      @mousedown="$emit('output-mousedown', $event, output)"
      @click.stop="$emit('output-click', $event, output)"
    >
      <!-- Loading Overlay - Show during routing operations -->
      <div 
        v-if="isRoutingLoading"
        class="absolute left-0 right-0 top-0 bottom-0 bg-slate-700/90 flex items-center justify-center z-40 rounded-sm backdrop-blur-xs routing-loading pointer-events-none">
        <div class="flex items-center space-x-2 text-green-400">
          <ma-loading class="bly-loading animate-spin text-lg"></ma-loading>
          <span class="text-sm font-medium">Routing...</span>
        </div>
      </div>

      <!-- Avatar -->
      <div class="flex justify-center pl-0">
        <el-avatar
          shape="square"
          size="medium"
          :style="{ background: outputColor }"
          :title="output.id"
        >
          {{ index + 1 }}
        </el-avatar>
      </div>

      <!-- Content -->
      <div class="min-w-0 select-none pointer-events-none relative" >
        <div class="text-white text-sm font-medium truncate pr-2">
          {{ output.displayName || output.name }}
        </div>
        <div class="text-xs leading-4 mt-0.5">
          <span
            v-if="output.routedInput"
            class="text-blue-400 truncate text-ellipsis"
          >
            {{ output.routedInput.name }}
          </span>
        </div>

        <!-- Output Operation Controls - Hover Overlay -->
        <div
          class="absolute top-0 right-0 h-full flex items-center px-2 bg-linear-to-l from-slate-800 via-slate-800/95 to-transparent opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-in-out transform translate-x-2 group-hover:translate-x-0 pointer-events-auto z-30"
          v-if="hasPermissions && !isRoutingLoading"
        >
          <div class="flex items-center space-x-1">
            <!-- Actions Menu (Ellipsis) -->
            <el-dropdown 
              @command="handleMenuCommand"
              trigger="click"
              placement="bottom-end"
              v-if="hasPermissions"
              @click.stop
            >
              <el-button
                size="mini"
                type="info"
                icon="el-icon-more"
                @click.stop
                class="opacity-90 hover:opacity-100 transition-opacity duration-150 focus:outline-hidden focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
                :aria-label="`Actions for ${output.name}`"
                :title="`Actions for ${output.name}`"
                tabindex="0"
                circle
              >
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <!-- OTT Output Actions -->
                <template v-if="output.outputType === 'OTT'">
                  <el-dropdown-item command="start" icon="el-icon-video-play">
                    Start Target
                  </el-dropdown-item>
                  <el-dropdown-item command="stop" icon="el-icon-video-pause" divided>
                    Stop Target
                  </el-dropdown-item>
                  <el-dropdown-item command="restart" icon="el-icon-refresh" v-if="isSysAdmin">
                    Restart Application
                  </el-dropdown-item>
                  <el-dropdown-item command="setStreamKey" icon="el-icon-key" divided v-if="isSysAdmin">
                    Set Stream Key
                  </el-dropdown-item>
                </template>

                <!-- ROUTE Output Actions -->
                <template v-if="output.outputType === 'ROUTE'">
                  <el-dropdown-item command="startInterface" icon="el-icon-connection">
                    Start Interface
                  </el-dropdown-item>
                  <el-dropdown-item command="stopInterface" icon="el-icon-close" divided>
                    Stop Interface
                  </el-dropdown-item>
                </template>

                <!-- Disconnect Route Action (for both OTT and ROUTE when routed) -->
                <template v-if="isOutputRouted">
                  <el-dropdown-item command="disconnect" icon="el-icon-close" divided>
                    Disconnect Route
                  </el-dropdown-item>
                </template>

                <!-- No actions available message for other output types -->
                <template v-if="!hasTypeSpecificActions && !isOutputRouted">
                  <el-dropdown-item disabled>
                    No actions available
                  </el-dropdown-item>
                </template>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'RouterOutputItem',
  
  props: {
    output: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    selectedOutput: {
      type: Object,
      default: null
    },
    selectedOutputIds: {
      type: Array,
      default: () => []
    },
    activeInputOutputs: {
      type: Array,
      default: () => []
    },
    outputColor: {
      type: String,
      required: true
    },
    hasPermissions: {
      type: Boolean,
      default: false
    },
    isRoutingLoading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      pendingAction: null,
      showConfirmation: false
    };
  },
  
  emits: [
    'output-mousedown',
    'output-click',
    'ott-operation',
    'route-operation',
    'show-stream-key-dialog',
    'disconnect-route'
  ],
  
  computed: {
    ...mapState({
      isSysAdmin: (state) => state.userAccount.isSysAdmin,
    }),
    isOutputRouted() {
      return this.output.routedInput?.id != null;
    },

    hasTypeSpecificActions() {
      return this.output.outputType === 'OTT' || this.output.outputType === 'ROUTE';
    },
    
    isOutputSelected() {
      return this.selectedOutputIds.includes(this.output.id) || 
        (this.selectedOutput && this.selectedOutput.id === this.output.id);
    }
  },

  methods: {
    handleMenuCommand(command) {
      const sensitiveActions = ['stop', 'stopInterface', 'disconnect'];
      
      if (sensitiveActions.includes(command)) {
        // Show confirmation dialog for sensitive actions
        this.$confirm(
          `Are you sure you want to ${this.getActionDisplayName(command)}? This action may affect live broadcasts.`,
          'Confirm Action',
          {
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            type: 'warning'
          }
        ).then(() => {
          this.executeAction(command);
        }).catch(() => {
          // User cancelled
        });
      } else {
        this.executeAction(command);
      }
    },

    executeAction(command) {
      switch (command) {
        case 'start':
        case 'stop':
        case 'restart':
          this.$emit('ott-operation', this.output, command);
          break;
        case 'setStreamKey':
          this.$emit('show-stream-key-dialog', this.output);
          break;
        case 'startInterface':
        case 'stopInterface':
          this.$emit('route-operation', this.output, command);
          break;
        case 'disconnect':
          this.$emit('disconnect-route', this.output);
          break;
      }
    },

    getActionDisplayName(command) {
      const displayNames = {
        'start': 'start the target',
        'stop': 'stop the target',
        'restart': 'restart the application',
        'setStreamKey': 'set the stream key',
        'startInterface': 'start the interface',
        'stopInterface': 'stop the interface',
        'disconnect': 'disconnect the route'
      };
      return displayNames[command] || command;
    }
  }
};
</script>

<style scoped>
/* Component-specific styles can be added here if needed */
/* Most styles are inherited from parent RouterAlt.vue */

/* Routing loading overlay animation */
.routing-loading {
  animation: routingPulse 1.5s ease-in-out infinite;
  transition: opacity 0.2s ease-in-out;
}

@keyframes routingPulse {
  0% { 
    opacity: 0.8;
    transform: scale(1);
  }
  50% { 
    opacity: 1;
    transform: scale(1.02);
  }
  100% { 
    opacity: 0.8;
    transform: scale(1);
  }
}
</style>
